/**
 * Generic Zustand resource store factory.
 *
 * Pairs with createResourceApi to give every CRUD module a consistent
 * slice with: items, item, loading, error, lastFetched, fetch/create/update/remove,
 * pagination, and a single setQuery helper.
 *
 * Pages then read state via the typed hook produced by this factory and
 * never reimplement loading/error/empty handling themselves.
 */

import { create, StateCreator, StoreApi, UseBoundStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  ListParams,
  ListResponse,
  PaginationMeta,
  ResourceApi,
} from '../services/createResourceApi';

export interface ResourceState<T, CreateInput = Partial<T>, UpdateInput = Partial<T>> {
  // Data
  items: T[];
  item: T | null;
  pagination: PaginationMeta | null;

  // Async state
  loading: boolean;
  saving: boolean;
  error: string | null;
  lastFetched: number | null;

  // Query state
  query: ListParams;

  // Actions
  setQuery: (q: Partial<ListParams>) => void;
  resetQuery: () => void;
  fetchList: (params?: ListParams) => Promise<ListResponse<T>>;
  fetchOne: (id: number | string) => Promise<T>;
  create: (input: CreateInput) => Promise<T>;
  update: (id: number | string, input: UpdateInput) => Promise<T>;
  remove: (id: number | string) => Promise<void>;
  setItem: (item: T | null) => void;
  reset: () => void;
}

export interface CreateResourceStoreOptions<T> {
  /**
   * Property used to identify a record (default: 'id').
   */
  idField?: keyof T;
  /**
   * Default query merged into every fetchList call.
   * Typically `{ page: 1, limit: 20 }` or similar.
   */
  defaultQuery?: ListParams;
  /**
   * Devtools name for the store (visible in Redux DevTools).
   */
  devtoolsName?: string;
}

function toErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Something went wrong';
}

export function createResourceStore<
  T extends Record<string, any>,
  CreateInput = Partial<T>,
  UpdateInput = Partial<T>,
>(
  api: ResourceApi<T, CreateInput, UpdateInput>,
  options: CreateResourceStoreOptions<T> = {},
): UseBoundStore<StoreApi<ResourceState<T, CreateInput, UpdateInput>>> {
  const idField = (options.idField ?? 'id') as keyof T;
  const defaultQuery: ListParams = { page: 1, limit: 20, ...(options.defaultQuery ?? {}) };

  const initial = {
    items: [] as T[],
    item: null as T | null,
    pagination: null as PaginationMeta | null,
    loading: false,
    saving: false,
    error: null as string | null,
    lastFetched: null as number | null,
    query: { ...defaultQuery },
  };

  const slice: StateCreator<ResourceState<T, CreateInput, UpdateInput>> = (set, get) => ({
    ...initial,

    setQuery: (patch) => {
      const next = { ...get().query, ...patch };
      // Reset to page 1 if a non-paging filter changes.
      const filterChanged = Object.keys(patch).some((k) => k !== 'page' && k !== 'limit');
      if (filterChanged) next.page = 1;
      set({ query: next });
    },

    resetQuery: () => set({ query: { ...defaultQuery } }),

    fetchList: async (params) => {
      const merged = { ...get().query, ...(params ?? {}) };
      set({ loading: true, error: null });
      try {
        const result = await api.list(merged);
        set({
          items: result.data,
          pagination: result.pagination ?? null,
          loading: false,
          lastFetched: Date.now(),
          query: merged,
        });
        return result;
      } catch (err) {
        set({ loading: false, error: toErrorMessage(err) });
        throw err;
      }
    },

    fetchOne: async (id) => {
      set({ loading: true, error: null });
      try {
        const item = await api.get(id);
        set({ item, loading: false });
        return item;
      } catch (err) {
        set({ loading: false, error: toErrorMessage(err) });
        throw err;
      }
    },

    create: async (input) => {
      set({ saving: true, error: null });
      try {
        const created = await api.create(input);
        set((s) => ({ items: [created, ...s.items], saving: false }));
        return created;
      } catch (err) {
        set({ saving: false, error: toErrorMessage(err) });
        throw err;
      }
    },

    update: async (id, input) => {
      set({ saving: true, error: null });
      try {
        const updated = await api.update(id, input);
        set((s) => ({
          items: s.items.map((it) =>
            String(it[idField]) === String(id) ? { ...it, ...updated } : it,
          ),
          item:
            s.item && String(s.item[idField]) === String(id)
              ? { ...s.item, ...updated }
              : s.item,
          saving: false,
        }));
        return updated;
      } catch (err) {
        set({ saving: false, error: toErrorMessage(err) });
        throw err;
      }
    },

    remove: async (id) => {
      set({ saving: true, error: null });
      try {
        await api.remove(id);
        set((s) => ({
          items: s.items.filter((it) => String(it[idField]) !== String(id)),
          item: s.item && String(s.item[idField]) === String(id) ? null : s.item,
          saving: false,
        }));
      } catch (err) {
        set({ saving: false, error: toErrorMessage(err) });
        throw err;
      }
    },

    setItem: (item) => set({ item }),

    reset: () => set({ ...initial, query: { ...defaultQuery } }),
  });

  return create<ResourceState<T, CreateInput, UpdateInput>>()(
    devtools(slice, { name: options.devtoolsName ?? 'resource-store' }),
  );
}
